<template>
    <!-- Dates -->
    <divider-generic
    icon="calendar"
    :applyBorder="false"
    :title="t('bankassurance.subscribe.travel.step1.datesTitle')"/>
    <infobox-generic :label="t('bankassurance.subscribe.travel.step1.extraInfo')"/>
    <!-- Start Date -->
    <input-calendar
    id="dataInicio"
    :params="dataInicioParams"
    :disabledDates="{
        to: !formData.dataFim || new Date(moment(formData.dataFim).subtract(89, 'days')) < new Date() ? new Date(): new Date(moment(formData.dataFim).subtract(89, 'days')),
        from: formData.dataFim ? new Date(moment(formData.dataFim).subtract(1, 'days')) : null
    }"
    :inputValue="formData.dataInicio"
    @input="[emit('formDataChange', { field: 'dataInicio', inputValue: $event }), clearFieldError('dataInicioParams')]"/>
    <!-- End Date -->
    <input-calendar
    id="dataFim"
    :params="dataFimParams"
    :disabledDates="{
        to: formData.dataInicio ? new Date(moment(formData.dataInicio).add(1, 'days')) : new Date(moment(new Date()).add(1, 'days')),
        from: formData.dataInicio ? new Date(moment(formData.dataInicio).add(89, 'days')) : null,
    }"
    :inputValue="formData.dataFim"
    @input="[emit('formDataChange', { field: 'dataFim', inputValue: $event }), clearFieldError('dataFimParams')]"/>
    <!-- People -->
    <divider-generic icon="person" :title="t('bankassurance.subscribe.travel.step1.peopleTitle')"/>
    <button-increment
    :params="numPessoasSegurasParams"
    :inputValue="formData.numPessoasSeguras"
    @valueChanged="[emit('formDataChange', { field: 'numPessoasSeguras', inputValue: $event })]"/>
<!-- Travel Data -->
    <divider-generic icon="trip" :title="t('bankassurance.subscribe.travel.step1.travelTitle')"/>
    <button-tertiary
    v-if="!formData.isCalculated"
    :class="{'mobile': getMobileAppState}"
    icon="open-new"
    :label="t('bankassurance.subscribe.travel.step1.infoLabel')"
    @clicked="informationHandler"/>
    <!-- Protection Plan -->
    <dropdown-generic
    id="planoProtecaoViagem"
    :params="planoProtecaoViagemParams"
    :options="getDomainData('TravelProtectionPlan').map(ele => {
        return { id: ele.code, text: ele.description }
    })"
    :selectedOption="formData.planoProtecaoViagem"
    @clicked="[emit('formDataChange', { field: 'planoProtecaoViagem', inputValue: $event }), clearFieldError('planoProtecaoViagemParams')]"/>
    <!-- Capital Scale -->
    <dropdown-generic
    id="escalaoCapitalViagem"
    :params="escalaoCapitalViagemParams"
    :options="getDomainData('TravelCapitalSscale').map(ele => {
        return { id: ele.code, text: ele.description }
    })"
    :selectedOption="formData.escalaoCapitalViagem"
    @clicked="[emit('formDataChange', { field: 'escalaoCapitalViagem', inputValue: $event }), clearFieldError('escalaoCapitalViagemParams')]"/>
    <!-- Covid in Travel -->
    <selectors-radio-group
    id="covidViagem"
    :label="covidViagemParams.label"
    :mandatory="covidViagemParams.mandatory"
    :errorState="covidViagemParams.errorState"
    :errorMessage="covidViagemParams.errorMessage"
    :radioParams="covidViagemParams.radioParams.map(ele => {
        return {
            ...ele,
            selected: formData.covidViagem === ele.id,
            errorState: covidViagemParams.errorState
        }
    })"
    @clicked="emit('formDataChange', { field: 'covidViagem', inputValue: $event })"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import InputCalendar from '@/components/generic/inputs/Calendar'
import ButtonIncrement from '@/components/generic/buttons/Increment'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import SelectorsRadioGroup from '@/components/generic/selectors/radio/RadioGroup'

/* Vue */
import { ref, watch } from 'vue'

/* Assets */
import moment from '@/assets/setups/moment'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['formDataChange', 'formErrorsCleared', 'mandatoryListChangeHandler', 'resetShowMandatoryErrorsProp', 'scrollTo'])

/* mapActions & mapGetters */
const {	getDomainData } = mapGetters(moduleMapper.BANKASSURANCE_SUBSCRIBE)
const { getMobileAppState } = mapGetters()

/* props received
@clearFormErrors { Boolean } - Prop to remove of form errors
@formData { Object } - object with info for our current form
@mandatoryList { Array } Array of mandatory keys corresponding with our form
@showMandatoryErrors { Boolean } Prop to trigger display of mandatory errors
*/
const props = defineProps({
    clearFormErrors: Boolean,
    formData: Object,
    mandatoryList: Array,
    showMandatoryErrors: Boolean
})

/**********************************************************************
*                             Start Date                              *
**********************************************************************/
const dataInicioParams = ref({
	labelText: t('bankassurance.subscribe.travel.step1.startDateLabel'),
    placeholder: t('bankassurance.subscribe.travel.step1.startDatePlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                              End Date                               *
**********************************************************************/
const dataFimParams = ref({
	labelText: t('bankassurance.subscribe.travel.step1.endDateLabel'),
    placeholder: t('bankassurance.subscribe.travel.step1.endDatePlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                           Number of Persons                         *
**********************************************************************/
const numPessoasSegurasParams = ref({
    labelText: t('bankassurance.subscribe.travel.step1.amountPeopleLabel'),
    mandatory: false,
    minValue: 1,
    maxValue: 9
})

/**********************************************************************
*                           Protection Plan                           *
**********************************************************************/
const planoProtecaoViagemParams = ref({
	labelText: t('bankassurance.subscribe.travel.step1.protectionPlanLabel'),
	placeholder: t('bankassurance.subscribe.travel.step1.protectionPlanPlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                              Capital Scale                          *
**********************************************************************/
const escalaoCapitalViagemParams = ref({
	labelText: t('bankassurance.subscribe.travel.step1.capitalScaleLabel'),
	placeholder: t('bankassurance.subscribe.travel.step1.capitalScalePlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                             Covid in Travel                         *
**********************************************************************/
const covidViagemParams = ref({
    label: t('bankassurance.subscribe.travel.step1.covidInTravelLabel'),
	mandatory: false,
	errorState: false,
    errorMessage: null,
    radioParams: [{
        id: 'S',
        text: t('bankassurance.subscribe.travel.step1.yes')
    }, {
        id: 'N',
        text: t('bankassurance.subscribe.travel.step1.no')
    }]
})

const informationHandler = () => {
    window.open(' https://www.nossaseguros.ao/insurance/private/9', '_blank')
}

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	dataInicioParams: dataInicioParams.value,
    dataFimParams: dataFimParams.value,
    planoProtecaoViagemParams: planoProtecaoViagemParams.value,
    escalaoCapitalViagemParams: escalaoCapitalViagemParams.value
})

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

	if (mandatoryFieldsEmptyList.length) {
        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    }
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}
</script>

<style lang="scss" scoped>
.tertiary-button-wrapper :deep(.button-tertiary) {
    width: fit-content;
}
</style>
